/**
 * FiServiceTypeSplitChart — donut of package vs consulting revenue
 * (adjustments shown as a muted third slice when present), with a
 * legend surfacing each side's COGS.
 */
import { useMemo } from 'react'
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts'
import type { ServiceTypeSplit } from './fiTypes'
import { formatCurrency } from './fiFormat'

interface FiServiceTypeSplitChartProps {
  serviceTypeSplit: ServiceTypeSplit
}

const PACKAGE_COLOR = '#3b82f6'
const CONSULTING_COLOR = '#8b5cf6'
const ADJUSTMENT_COLOR = '#475569'

interface TooltipPayloadEntry {
  payload?: { name: string; value: number }
}

function SplitTooltip({ active, payload }: { active?: boolean; payload?: TooltipPayloadEntry[] }) {
  if (!active || !payload || payload.length === 0) return null
  const p = payload[0]
  return (
    <div className="bg-gray-800 border border-gray-600 rounded-lg shadow-lg p-2.5 text-sm">
      <span className="text-white font-medium">{p.payload?.name}</span>
      <span className="text-gray-300 ml-2">{formatCurrency(p.payload?.value ?? 0)}</span>
    </div>
  )
}

export default function FiServiceTypeSplitChart({ serviceTypeSplit }: FiServiceTypeSplitChartProps) {
  const { packageRevenue, consultingRevenue, adjustmentRevenue, packageCogs, consultingCogs } = serviceTypeSplit
  const total = packageRevenue + consultingRevenue + adjustmentRevenue

  const data = useMemo(() => {
    const rows = [
      { name: 'Paquete', value: packageRevenue, color: PACKAGE_COLOR },
      { name: 'Consultoría', value: consultingRevenue, color: CONSULTING_COLOR },
    ]
    if (adjustmentRevenue !== 0) rows.push({ name: 'Ajustes', value: adjustmentRevenue, color: ADJUSTMENT_COLOR })
    return rows
  }, [packageRevenue, consultingRevenue, adjustmentRevenue])

  const hasData = total !== 0

  return (
    <div className="bg-gray-800 rounded-xl p-6">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-white">Paquete vs Consultoría</h3>
        <p className="text-gray-400 text-sm">Composición del revenue del período</p>
      </div>

      {!hasData ? (
        <div className="text-center py-8 text-gray-400">Sin datos suficientes.</div>
      ) : (
        <div className="flex items-center gap-6">
          <div className="w-36 h-36 flex-shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data} dataKey="value" nameKey="name" innerRadius={40} outerRadius={64} paddingAngle={2}>
                  {data.map(d => <Cell key={d.name} fill={d.color} stroke="none" />)}
                </Pie>
                <Tooltip content={<SplitTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex-1 space-y-3">
            <div>
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2 text-sm">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: PACKAGE_COLOR }} />
                  <span className="text-white font-medium">Paquete</span>
                </span>
                <span className="text-gray-300 text-sm">{formatCurrency(packageRevenue)}</span>
              </div>
              <div className="text-xs text-gray-500 mt-0.5 pl-[18px]">COGS: {formatCurrency(packageCogs)}</div>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2 text-sm">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: CONSULTING_COLOR }} />
                  <span className="text-white font-medium">Consultoría</span>
                </span>
                <span className="text-gray-300 text-sm">{formatCurrency(consultingRevenue)}</span>
              </div>
              <div className="text-xs text-gray-500 mt-0.5 pl-[18px]">COGS: {formatCurrency(consultingCogs)}</div>
            </div>
            {adjustmentRevenue !== 0 && (
              <div>
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2 text-sm">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: ADJUSTMENT_COLOR }} />
                    <span className="text-white font-medium">Ajustes</span>
                  </span>
                  <span className="text-gray-300 text-sm">{formatCurrency(adjustmentRevenue)}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
